import { ANALYTICS_EVENTS } from '@syncanix/shared';
import {
  FlaskConical,
  Languages,
  MessagesSquare,
  ScanSearch,
  ShieldCheck,
  UserCheck,
  Users,
  Wrench,
} from 'lucide-react';

import { track } from '@/analytics/track';
import { ctaClasses, Medallion, ACTION_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import {
  BUILD_REASONS,
  BUILD_SCOPE_ITEMS,
  BUY_REASONS,
  DecisionTable,
  EVIDENCE_STATS,
  IN_HOUSE_ANCHORS,
  ITEM_CARD,
  ITEM_DETAIL,
  ITEM_TITLE,
  SECTION_HEADING,
  SECTION_INTRO,
  TIMELINE_STAGES,
} from '@/features/build-vs-buy';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';
import { salesContactHref } from '@/lib/site-url';

import type { MedallionHue } from '@/components/bedrock';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

/**
 * A mark per scope item, hued by which kind of work it is: the product surface
 * the team would have to recreate (cyan), identity (blue), the things that go
 * wrong at runtime (violet), and the work that never ends (purple). Keyed by
 * the SOT id, so a ninth scope item renders without a medallion rather than
 * with a wrong one — the mark is a scanning aid, and a missing one costs less
 * than a misleading one.
 */
const SCOPE_MARK: Readonly<Record<string, { icon: LucideIcon; hue: MedallionHue }>> = {
  surface: { icon: MessagesSquare, hue: 'cyan' },
  discovery: { icon: ScanSearch, hue: 'cyan' },
  auth: { icon: UserCheck, hue: 'blue' },
  safety: { icon: ShieldCheck, hue: 'violet' },
  tenancy: { icon: Users, hue: 'blue' },
  i18n: { icon: Languages, hue: 'cyan' },
  evals: { icon: FlaskConical, hue: 'purple' },
  maintenance: { icon: Wrench, hue: 'purple' },
};

/**
 * The build-vs-buy decision page at `/build-vs-buy` (canonical; the historical
 * `/pricing/build-vs-buy` path renders the same component and canonicalizes
 * here).
 *
 * The page argues the READER's decision from the reader's chair: their product
 * needs an agent surface, and the question is whether their own team builds it
 * or buys it. Five moves: (1) what an in-house build actually includes — the
 * surface itself plus everything that never shows up in the demo, (2) how the
 * build year actually goes, (3) the published delivery-risk data on in-house
 * initiatives, with named sources, (4) an honest split of when building is the
 * right call and when buying is, (5) the two paths side by side, ending in the
 * cost math. No competitor appears anywhere — a reader weighing build vs buy
 * is not comparison shopping, and the test suite pins that framing. Every
 * number traces to a [V] entry in
 * `product/market-and-gtm/market-reality-check-2026-06.md`.
 */
export function BuildVsBuy(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('pricing.bvb.page.title'),
    description: t('pricing.bvb.page.description'),
    // Always the new path, including when this component renders at the old
    // one — that is what consolidates the two URLs onto one page.
    path: '/build-vs-buy',
  });

  // One params object for every interpolating string on the page, so each
  // figure has exactly one home in the SOT (extra params are ignored by keys
  // that take none).
  const anchors = {
    engineerCost: IN_HOUSE_ANCHORS.engineerCost,
    starterPrice: IN_HOUSE_ANCHORS.starterPrice,
    threshold: IN_HOUSE_ANCHORS.buyThreshold,
  };

  return (
    <main id="main" tabIndex={-1} className="min-h-screen bg-[color:var(--bd-page)]">
      <PageHero
        width="prose"
        align="start"
        title={t('pricing.bvb.page.heading')}
        description={t('pricing.bvb.page.intro')}
      />
      <Container width="prose" className="pb-16 pt-4 sm:pb-20">
        <section aria-labelledby="bvb-scope-heading" className="mt-10">
          <h2 id="bvb-scope-heading" className={SECTION_HEADING}>
            {t('pricing.bvb.scope.heading')}
          </h2>
          <p className={SECTION_INTRO}>{t('pricing.bvb.scope.intro')}</p>
          {/* Eight work items, and the reader is scanning for the ones their
              team has not thought about yet. As identical white cards they had
              no entry point — a medallion per item is the cheapest fixed point
              to scan by, and it is the one place on this page the brand ramp
              appears. */}
          <ul className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {BUILD_SCOPE_ITEMS.map((item) => {
              const mark = SCOPE_MARK[item.id];
              return (
                <li
                  key={item.id}
                  data-scope={item.id}
                  className="flex gap-3.5 rounded-lg border border-[color:var(--bd-line)] p-4"
                >
                  {mark ? <Medallion icon={mark.icon} hue={mark.hue} size="sm" /> : null}
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className={ITEM_TITLE}>{t(item.titleKey)}</span>
                    <span className={ITEM_DETAIL}>{t(item.detailKey)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section aria-labelledby="bvb-timeline-heading" className="mt-10">
          <h2 id="bvb-timeline-heading" className={SECTION_HEADING}>
            {t('pricing.bvb.timeline.heading')}
          </h2>
          <p className={SECTION_INTRO}>{t('pricing.bvb.timeline.intro')}</p>
          <ol className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {TIMELINE_STAGES.map((stage, index) => (
              <li key={stage.id} data-stage={stage.id} className={ITEM_CARD}>
                <span
                  aria-hidden="true"
                  className="font-mono text-xs text-[color:color-mix(in_srgb,var(--bd-muted)_80%,transparent)]"
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className={ITEM_TITLE}>{t(stage.titleKey)}</span>
                <span className={ITEM_DETAIL}>{t(stage.detailKey)}</span>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="bvb-evidence-heading" className="mt-10">
          <h2 id="bvb-evidence-heading" className={SECTION_HEADING}>
            {t('pricing.bvb.evidence.heading')}
          </h2>
          <p className={SECTION_INTRO}>{t('pricing.bvb.evidence.intro')}</p>
          <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {EVIDENCE_STATS.map((stat) => (
              <div key={stat.id} data-evidence={stat.id} className={ITEM_CARD}>
                <dt className="text-2xl font-semibold tracking-tight text-[color:var(--bd-ink)]">
                  {stat.figure}
                </dt>
                <dd className={ITEM_DETAIL}>
                  {t(stat.claimKey)}
                  <span className="mt-1 block text-xs text-[color:color-mix(in_srgb,var(--bd-muted)_80%,transparent)]">
                    {stat.source}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section aria-labelledby="bvb-when-heading" className="mt-10">
          <h2 id="bvb-when-heading" className={SECTION_HEADING}>
            {t('pricing.bvb.when.heading')}
          </h2>
          <p className={SECTION_INTRO}>{t('pricing.bvb.when.intro')}</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-[color:var(--bd-line)] p-4">
              <h3 className="text-base font-semibold text-[color:var(--bd-ink)]">
                {t('pricing.bvb.whenBuild.heading')}
              </h3>
              <ul className="mt-3 flex flex-col gap-3">
                {BUILD_REASONS.map((reason) => (
                  <li key={reason.id} data-when-build={reason.id} className="flex flex-col gap-1">
                    <span className={ITEM_TITLE}>{t(reason.titleKey)}</span>
                    <span className={ITEM_DETAIL}>{t(reason.detailKey)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-[color:var(--bd-line)] p-4">
              <h3 className="text-base font-semibold text-[color:var(--bd-ink)]">
                {t('pricing.bvb.whenBuy.heading')}
              </h3>
              <ul className="mt-3 flex flex-col gap-3">
                {BUY_REASONS.map((reason) => (
                  <li key={reason.id} data-when-buy={reason.id} className="flex flex-col gap-1">
                    <span className={ITEM_TITLE}>{t(reason.titleKey)}</span>
                    <span className={ITEM_DETAIL}>{t(reason.detailKey)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-[color:var(--bd-muted)]">
            {t('pricing.bvb.evidence.threshold', anchors)}
          </p>
        </section>

        <DecisionTable anchors={anchors} />

        <section aria-labelledby="bvb-math-heading" className="mt-10">
          <h2 id="bvb-math-heading" className={SECTION_HEADING}>
            {t('pricing.bvb.math.heading')}
          </h2>
          <p className={SECTION_INTRO}>{t('pricing.bvb.math.body', anchors)}</p>
        </section>

        <section
          aria-label={t('pricing.bvb.cta.regionLabel')}
          className="mt-12 flex flex-col items-start gap-4 border-t border-[color:var(--bd-line)] pt-8 sm:flex-row sm:items-center sm:justify-between"
        >
          <Link to="/pricing" className={`inline-flex items-center gap-1 text-sm ${ACTION_LINK}`}>
            {t('pricing.bvb.cta.seePricing')}
          </Link>
          {/* Sales-led acquisition: routes to the /contact form like every
              pricing-tier CTA — no payment processor is connected, so there is
              no self-serve trial to start. */}
          <Link
            to={salesContactHref()}
            className={ctaClasses({ size: 'sm' })}
            onClick={() => {
              track(ANALYTICS_EVENTS.MARKETING_CONTACT_CTA_CLICKED, { location: 'build-vs-buy' });
            }}
          >
            {t('pricing.bvb.cta.sales')}
          </Link>
        </section>
      </Container>
    </main>
  );
}
